import React, { useState, useMemo } from 'react';
import {
  Users,
  Search,
  ShieldCheck,
  GraduationCap,
  UserCog,
  Loader2,
  Mail,
  Calendar,
  CheckCircle2,
  XCircle,
  Plus,
  Minus,
  MessageCircle,
} from 'lucide-react';
import {
  useGetAdminUsersQuery,
  useUpdateAdminUserRolesMutation,
} from '../../api/apiSlice';
import toast from 'react-hot-toast';
import Pagination from '../../components/common/Pagination';
import ConfirmDialog from '../../components/common/ConfirmDialog';

// AdminUsersPage
// ──────────────
// Список юзеров с поиском, фильтром по роли, пагинацией. Каждая строка —
// быстрые действия: повысить до instructor/admin или снять роль.
//
// Безопасность: бэк сам проверяет «нельзя снять с себя», «нельзя снять
// последнего админа», «нельзя выставить произвольную роль». UI только
// удобство; настоящая защита там.

const ROLE_LABEL = {
  student: { label: 'Студент', icon: GraduationCap, color: 'blue' },
  instructor: { label: 'Инструктор', icon: UserCog, color: 'purple' },
  admin: { label: 'Админ', icon: ShieldCheck, color: 'rose' },
};

const ROLE_PALETTE = {
  blue: 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300',
  purple: 'bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300',
  rose: 'bg-rose-100 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300',
};

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

const RoleBadge = ({ role }) => {
  const def = ROLE_LABEL[role];
  if (!def) return null;
  const Icon = def.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${ROLE_PALETTE[def.color]}`}
    >
      <Icon className="w-3 h-3" />
      {def.label}
    </span>
  );
};

const AdminUsersPage = () => {
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [page, setPage] = useState(1);
  const [pendingAction, setPendingAction] = useState(null);
  // {userId, name, add?, remove?, role}

  const { data, isLoading, isFetching, error } = useGetAdminUsersQuery({
    search: search.trim(),
    role,
    page,
    limit: 20,
  });
  const [updateRoles, { isLoading: isUpdating }] = useUpdateAdminUserRolesMutation();

  const items = data?.items || [];

  // Reset page on search/filter change.
  React.useEffect(() => {
    setPage(1);
  }, [search, role]);

  const confirmAction = (user, action, targetRole) => {
    setPendingAction({
      userId: user._id,
      name: user.name || user.email,
      [action]: targetRole,
      role: targetRole,
      verb: action === 'add' ? 'добавить' : 'снять',
    });
  };

  const runAction = async () => {
    if (!pendingAction) return;
    try {
      await updateRoles({
        userId: pendingAction.userId,
        add: pendingAction.add,
        remove: pendingAction.remove,
      }).unwrap();
      toast.success('Роли обновлены');
    } catch (err) {
      toast.error(err?.data?.message || 'Не удалось изменить роль');
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Управление пользователями
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Поиск по имени и email. Повышайте до инструктора или админа, снимайте роли.
        </p>
      </div>

      {/* Toolbar */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по email, имени или ID пользователя…"
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:w-48"
          >
            <option value="">Все роли</option>
            <option value="student">Только студенты</option>
            <option value="instructor">Только инструкторы</option>
            <option value="admin">Только админы</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 p-4 text-sm text-red-700 dark:text-red-300">
          {error?.data?.message || 'Не удалось загрузить пользователей'}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-12 text-center text-gray-500 dark:text-gray-400">
          <Users className="w-10 h-10 mx-auto mb-2" />
          Пользователи не найдены
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Пользователь</th>
                    <th className="px-4 py-3 text-left font-semibold">Роли</th>
                    <th className="px-4 py-3 text-right font-semibold">Активность</th>
                    <th className="px-4 py-3 text-right font-semibold">Действия</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {items.map((u) => {
                    const isInstructor = u.roles?.includes('instructor');
                    const isAdmin = u.roles?.includes('admin');
                    return (
                      <tr
                        key={u._id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition"
                      >
                        <td className="px-4 py-3 max-w-[280px]">
                          <div className="flex items-center gap-3">
                            {/* В админ-таблице НЕ грузим avatarUrl с R2: на странице
                                100+ юзеров → 100+ Cloudflare GET'ов на каждое
                                открытие = деньги/квота. Показываем initials-кружок,
                                для модерации этого достаточно (имя+email видны рядом). */}
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold shrink-0">
                              {(u.name || u.email || '?').slice(0, 1).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-900 dark:text-white truncate flex items-center gap-1.5">
                                {u.name || '—'}
                                {u.isVerified ? (
                                  <CheckCircle2
                                    className="w-3.5 h-3.5 text-emerald-500 shrink-0"
                                    title="Email подтверждён"
                                  />
                                ) : (
                                  <XCircle
                                    className="w-3.5 h-3.5 text-gray-400 shrink-0"
                                    title="Email не подтверждён"
                                  />
                                )}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 truncate">
                                <Mail className="w-3 h-3 shrink-0" />
                                {u.email}
                              </div>
                              {/* Контактный WhatsApp — заводят инструкторы в профиле,
                                  здесь это deep-link wa.me для прямой связи менеджера.
                                  stopPropagation чтобы клик не триггерил никакие
                                  родительские хэндлеры. */}
                              {u.contactPhone && (
                                <a
                                  href={`https://wa.me/${u.contactPhone.replace(/[^\d]/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1 truncate font-medium"
                                  title="Открыть WhatsApp"
                                >
                                  <MessageCircle className="w-3 h-3 shrink-0" />
                                  {u.contactPhone}
                                </a>
                              )}
                              {/* ID — копируется одним кликом. Полезно когда
                                  админу скидывают «у этого юзера баг» с ID. */}
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard?.writeText(u._id).catch(() => {});
                                  toast.success('ID скопирован');
                                }}
                                title="Скопировать ID"
                                className="text-[10px] text-gray-400 dark:text-gray-500 font-mono truncate hover:text-indigo-600 dark:hover:text-indigo-400"
                              >
                                {u._id}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(u.roles || []).map((r) => (
                              <RoleBadge key={r} role={r} />
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                          <div>📚 {u.enrollmentsCount} куплено</div>
                          {u.coursesCount > 0 && <div>🎓 {u.coursesCount} создано</div>}
                          <div className="flex items-center justify-end gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            {fmtDate(u.createdAt)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1.5 items-end">
                            {!isInstructor ? (
                              <button
                                type="button"
                                onClick={() => confirmAction(u, 'add', 'instructor')}
                                disabled={isUpdating}
                                className="inline-flex items-center gap-1 rounded-lg border border-purple-200 dark:border-purple-900/40 bg-purple-50 dark:bg-purple-950/30 hover:bg-purple-100 dark:hover:bg-purple-950/50 text-purple-700 dark:text-purple-300 px-2.5 py-1.5 text-xs font-medium"
                              >
                                <Plus className="w-3 h-3" /> Инструктор
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => confirmAction(u, 'remove', 'instructor')}
                                disabled={isUpdating}
                                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 px-2.5 py-1.5 text-xs font-medium"
                              >
                                <Minus className="w-3 h-3" /> Инструктор
                              </button>
                            )}
                            {!isAdmin ? (
                              <button
                                type="button"
                                onClick={() => confirmAction(u, 'add', 'admin')}
                                disabled={isUpdating}
                                className="inline-flex items-center gap-1 rounded-lg border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/30 hover:bg-rose-100 dark:hover:bg-rose-950/50 text-rose-700 dark:text-rose-300 px-2.5 py-1.5 text-xs font-medium"
                              >
                                <Plus className="w-3 h-3" /> Админ
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => confirmAction(u, 'remove', 'admin')}
                                disabled={isUpdating}
                                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 px-2.5 py-1.5 text-xs font-medium"
                              >
                                <Minus className="w-3 h-3" /> Админ
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {data?.pageCount > 1 && (
            <Pagination
              page={page}
              pageCount={data.pageCount}
              onChange={(p) => setPage(p)}
            />
          )}

          <p className="text-xs text-gray-500 dark:text-gray-400">
            Всего: {data?.total || 0} · Страница {page} из {data?.pageCount || 1}
            {isFetching && ' · обновляется…'}
          </p>
        </>
      )}

      <ConfirmDialog
        open={Boolean(pendingAction)}
        title={pendingAction ? `${pendingAction.verb} роль «${ROLE_LABEL[pendingAction.role]?.label}»?` : ''}
        description={pendingAction ? `Пользователь: ${pendingAction.name}` : ''}
        confirmLabel={pendingAction?.verb === 'снять' ? 'Снять' : 'Подтвердить'}
        destructive={pendingAction?.verb === 'снять'}
        loading={isUpdating}
        onConfirm={runAction}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
};

export default AdminUsersPage;
